// Workspace context management hook

import { useState, useCallback } from 'react';
import {
  WorkspaceContext,
  WorkspaceContextActions,
  initialWorkspaceContext,
} from '@/types/workspaceContext';
import { Workspace } from '@/types/workspace';

export function useWorkspaceContext(): [WorkspaceContext, WorkspaceContextActions] {
  const [workspaceContext, setWorkspaceContextState] =
    useState<WorkspaceContext>(initialWorkspaceContext);

  const setWorkspaceContext = useCallback((context: WorkspaceContext) => {
    setWorkspaceContextState(context);
  }, []);

  const markWorkspaceAsChanged = useCallback(() => {
    if (workspaceContext.isWorkspaceLoaded) {
      setWorkspaceContextState((prev) => ({
        ...prev,
        currentWorkspace: {
          ...prev.currentWorkspace,
          hasUnsavedChanges: true,
        },
      }));
    }
  }, [workspaceContext.isWorkspaceLoaded]);

  const clearWorkspaceContext = useCallback(() => {
    setWorkspaceContextState(initialWorkspaceContext);
  }, []);

  const updateWorkspaceAfterSave = useCallback((workspace: Workspace) => {
    setWorkspaceContextState({
      currentWorkspace: {
        id: workspace.id,
        name: workspace.name,
        version: workspace.metadata.version,
        lastSaved: workspace.metadata.updatedAt,
        hasUnsavedChanges: false, // Reset dirty flag
      },
      isWorkspaceLoaded: true,
    });
  }, []);

  const actions: WorkspaceContextActions = {
    setWorkspaceContext,
    markWorkspaceAsChanged,
    clearWorkspaceContext,
    updateWorkspaceAfterSave,
  };

  return [workspaceContext, actions];
}

/**
 * Helper function to set workspace context after loading
 */
export function createWorkspaceContextFromLoaded(workspace: Workspace): WorkspaceContext {
  return {
    currentWorkspace: {
      id: workspace.id,
      name: workspace.name,
      version: workspace.metadata.version,
      lastSaved: workspace.metadata.updatedAt,
      hasUnsavedChanges: false,
    },
    isWorkspaceLoaded: true,
  };
}
